import { IMaskInput } from 'react-imask';
import { useState, forwardRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField, { TextFieldProps } from '@mui/material/TextField';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

type MaskType = 'phone' | 'ssn' | 'ein' | 'routing' | 'digits';

interface MaskConfig {
  mask: string;
  definitions?: Record<string, RegExp>;
  lazy?: boolean;
}

const MASK_CONFIGS: Record<MaskType, MaskConfig> = {
  phone: {
    mask: '(000) 000-0000',
    lazy: false,
  },
  ssn: {
    mask: '000-00-0000',
    lazy: false,
  },
  ein: {
    mask: '00-0000000',
    lazy: false,
  },
  routing: {
    mask: '000000000',
    lazy: false,
  },
  digits: {
    mask: '0000000000000000000',
    lazy: true,
  },
};

// ----------------------------------------------------------------------

interface CustomMaskInputProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  maskType: MaskType;
}

const CustomMaskInput = forwardRef<HTMLInputElement, CustomMaskInputProps>(
  (props, ref) => {
    const { onChange, maskType, ...other } = props;
    const config = MASK_CONFIGS[maskType];

    return (
      <IMaskInput
        {...other}
        mask={config.mask}
        definitions={config.definitions}
        lazy={config.lazy}
        inputRef={ref}
        onAccept={(value: string) =>
          onChange({ target: { name: props.name, value } })
        }
        overwrite
      />
    );
  }
);

// ----------------------------------------------------------------------

type RHFMaskedPasswordFieldProps = Omit<TextFieldProps, 'name'> & {
  name: string;
  maskType: MaskType;
};

export default function RHFMaskedPasswordField({
  name,
  maskType,
  helperText,
  InputLabelProps,
  ...other
}: RHFMaskedPasswordFieldProps) {
  const { control } = useFormContext();
  const [showPassword, setShowPassword] = useState(false);

  // For non-lazy masks, force the label to shrink to avoid overlap with mask placeholder
  const config = MASK_CONFIGS[maskType];
  const shouldShrinkLabel = config.lazy === false;

  const handleToggleVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          fullWidth
          type={showPassword ? 'text' : 'password'}
          error={!!error}
          helperText={error ? error?.message : helperText}
          InputProps={{
            inputComponent: CustomMaskInput as any,
            inputProps: {
              maskType,
            },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleToggleVisibility}
                  edge="end"
                  size="small"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Iconify
                    icon={showPassword ? 'eva:eye-off-fill' : 'eva:eye-fill'}
                    width={20}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
          InputLabelProps={{
            ...(shouldShrinkLabel && { shrink: true }),
            ...InputLabelProps,
          }}
          {...other}
        />
      )}
    />
  );
}
